<template>
    <div class="multi-step-filter-dropdown">
        <template v-if="usePopover">
            <button ref="trigger"
                type="button"
                class="f fh fa-c dropdown-select-trigger dw-10"
                :class="['el--t-' + btnType, 'el--s-' + btnSize]"
                :disabled="disabled"
                @click.prevent.stop="onToggle">
                <span v-if="hasSelection">{{ uiSelectedItem }}</span>
                <span class="dropdown-placeholder" v-else-if="placeholder">{{ placeholder | athT }}</span>
                <span class="fg"></span>
                <i class="sm-l-s sm-r-xs lnr-chevron-down"></i>
            </button>
        </template>
        <Dropdown v-else uiBtnDisplay="select" :uiBtnSize="btnSize" :pubkey="uid" :uiAdjustable="!disabled">
            <template #btn-content>
                <span v-if="hasSelection">{{ uiSelectedItem }}</span>
                <span class="dropdown-placeholder" v-else-if="placeholder">{{ placeholder | athT }}</span>
            </template>
            <MultiStepFilter :title="title"
                :items="items"
                :selectedItems="internalSelectedItems"
                :restrictedItems="restrictedItemsForMode"
                :max="max"
                :min="min"
                :includeGroups="includeGroups"
                :hideSearch="hideSearch"
                :hideBreadcrumbs="hideBreadcrumbs"
                :showClearSelection="showClearSelection"
                :allowGroupSelection="allowGroupSelection"
                :isolateSelection="isolateSelection"
                :showBadgeCounts="showBadgeCounts"
                :startingItem="selectedItem || normalizedStartingItem"
                :mode="mode"
                :uiHeight="uiHeight"
                @onItemSelected="onItemSelected"
                @onCheck="onCheck" />
        </Dropdown>
    </div>
</template>

<script>
import { guuid, isNeObject } from '@aretheyhappy/utils';
import MultiStepFilterPopover from '../multi-step-filter-popover/MultiStepFilterPopover.vue';
import MultiStepFilter from '../multi-step-filter/MultiStepFilter.vue';
import Dropdown from '../Dropdown.vue';

export default {
    name: 'mead-multi-step-filter-dropdown',
    props: {
        /**
         * Placeholder text to be shown when there is nothing selected.
         */
        placeholder: { type: String, default: null },
        /**
         * Style variant for the trigger button.
         */
        btnType: { type: String, default: 'default' },
        /**
         * Size of the trigger button.
         */
        btnSize: { type: String, default: 'm' },
        /**
         * Whether the component should be in an enabled or disabled state.
         */
        disabled: { type: Boolean, default: false },
        /**
         * Whether to use a popover (with a button trigger) or an inline dropdown to render the
         * filter.
         */
        usePopover: { type: Boolean, default: true },
        /**
         * The stacking index of this dropdown when it is itself rendered inside another popover;
         * forwarded so a popover opened from it can compute the index it should stack above.
         */
        popoverIndex: { type: [Number, String], default: 1},

        /**
         * Title/header text shown above the item list, forwarded as the `title` prop to the
         * inline MultiStepFilter (when usePopover is false) or the MultiStepFilterPopover (when
         * usePopover is true).
         */
        title: {type: String, default: 'mead.options' },
        /**
         * The hierarchical list of selectable items rendered by the inline MultiStepFilter (when
         * usePopover is false) or passed to the MultiStepFilterPopover (when usePopover is true).
         */
        items: {type: Array, default: () => []},
        /**
         * The initially/externally selected value(s), normalized (including nested arrays/objects)
         * into the internal selection used to seed the dropdown's current selection.
         */
        selectedItems: {type: Array, default: () => []},
        /**
         * Items that cannot be selected, normalized and forwarded to the inline filter or popover.
         */
        restrictedItems: {type: Array, default: () => []},
        /**
         * Maximum number of items that may be selected; forwarded to the underlying MultiStepFilter.
         */
        max: {type: Number, default: 0},
        /**
         * Minimum number of items that must remain selected; forwarded to the underlying MultiStepFilter.
         */
        min: {type: Number, default: 0},
        /**
         * Whether group items are included as selectable entries; forwarded to the underlying
         * MultiStepFilter.
         */
        includeGroups: {type: Boolean, default: true},
        /**
         * Hide the search input above the list; forwarded to the inner MultiStepFilter.
         */
        hideSearch: {type: Boolean, default: false},
        /**
         * Hide the breadcrumb trail above the list; forwarded to the inner MultiStepFilter.
         * Only use with flat lists, not grouped data: without breadcrumbs there is no way to
         * navigate back out of a group.
         */
        hideBreadcrumbs: {type: Boolean, default: false},
        /**
         * Show a "Clear" action that removes the entire selection; forwarded to the inner
         * MultiStepFilter.
         */
        showClearSelection: {type: Boolean, default: false},
        /**
         * Whether an entire group can be selected as a single value; forwarded to the underlying
         * MultiStepFilter.
         */
        allowGroupSelection: {type: Boolean, default: false},
        /**
         * Whether selecting a group should only include the group value itself rather than its
         * children's values; forwarded to the underlying MultiStepFilter.
         */
        isolateSelection: {type: Boolean, default: false},
        /**
         * Whether to show badges with selected-descendant counts on group items; forwarded to the
         * underlying MultiStepFilter.
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * The item (or items) the underlying filter should navigate to/preselect on open when no
         * explicit selection has been made yet.
         */
        startingItem: {type: [String, Array, Object], default: undefined},
        /**
         * Height of the underlying filter's scrollable item list.
         */
        uiHeight: {type: [String, Number], default: '40'},
        /**
         * The selection mode passed to the underlying MultiStepFilter (e.g. single vs. multi
         * selection), controlling how selections are applied and when the popover auto-closes.
         */
        mode: {type: String, default: () => MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE}
    },
    components: {
        MultiStepFilter,
        Dropdown
    },
    data() {
        const selectedItems = this.normalizeSelection(this.selectedItems);
        const startingItems = this.normalizeSelection(this.startingItem);
        const internalSelectedItems = selectedItems.length > 0 ? selectedItems : startingItems;

        return {
            uid: guuid(),
            selectedItem: internalSelectedItems[0] || null,
            internalSelectedItems,
            isPopoverOpen: false,
        };
    },
    methods: {
        normalizeSelection(selection) {
            if (Array.isArray(selection)) {
                return selection.reduce((items, value) => items.concat(this.normalizeSelection(value)), []);
            }
            if (isNeObject(selection)) {
                return Object.values(selection)
                    .reduce((items, value) => items.concat(this.normalizeSelection(value)), []);
            }
            return selection ? [selection] : [];
        },
        setInternalSelection(selection, fallback = null) {
            this.internalSelectedItems = this.normalizeSelection(selection);
            this.selectedItem = this.internalSelectedItems[0] || fallback;
        },
        onToggle: function (event) {
            if (this.isPopoverOpen) {
                MeadCommon.PopoverService.close();
                return;
            }

            const config = new MeadCommon.PopoverConfig()
                .setComponent(MultiStepFilterPopover)
                .setMovable(true)
                .setNoPad(true)
                .setEvent(event)
                .setNext(() => {
                    this.isPopoverOpen = false;
                })
                .setProps({
                    title: this.title,
                    pubkey: this.uid,
                    items: this.items,
                    selectedItems: this.internalSelectedItems,
                    restrictedItems: this.restrictedItemsForMode,
                    max: this.max,
                    min: this.min,
                    includeGroups: this.includeGroups,
                    hideSearch: this.hideSearch,
                    hideBreadcrumbs: this.hideBreadcrumbs,
                    showClearSelection: this.showClearSelection,
                    allowGroupSelection: this.allowGroupSelection,
                    isolateSelection: this.isolateSelection,
                    mode: this.mode,
                    showBadgeCounts: this.showBadgeCounts,
                    startingItem: this.selectedItem || this.normalizedStartingItem,
                    uiHeight: this.uiHeight
                });
            this.isPopoverOpen = true;
            MeadCommon.PopoverService.open(config);
        },
        onItemSelected(selection) {
            const selectedItems = this.normalizeSelection(selection);
            this.setInternalSelection(selectedItems);
            this.$emit('onItemSelected', selectedItems[0]);
            MeadCommon.Flash.publish(`${this.uid}:close`);
        },
        onCheck(selection) {
            const selectedItems = this.normalizeSelection(selection);
            this.setInternalSelection(selectedItems);
            this.$emit('onCheck', selectedItems);
            this.$emit('onItemSelected', selectedItems);
        },
        onPopoverItemSelected({ value }) {
            this.onItemSelected(value);
        },
        onPopoverCheck(value) {
            this.onCheck(value);
        },
        findItemWithValue: function (list, value, items=[]) {
            for (const item of list) {
                if (item.value === value) items.push(item);
                if (item.isGroup && items.length === 0) {
                    this.findItemWithValue(item.children, value, items);
                }
            }
        },
        getLabelForValue: function (value) {
            const found = [];
            this.findItemWithValue(this.items, value, found);
            return found.length > 0 ? MeadCommon.I18N.go(found[0].label) : value;
        },
    },
    computed: {
        normalizedSelectedItems: function () {
            return this.normalizeSelection(this.selectedItems);
        },
        normalizedRestrictedItems: function () {
            return this.normalizeSelection(this.restrictedItems);
        },
        restrictedItemsForMode: function () {
            return this.normalizedRestrictedItems;
        },
        normalizedStartingItem: function () {
            return this.normalizeSelection(this.startingItem)[0];
        },
        hasSelection: function () {
            return this.internalSelectedItems.length > 0;
        },
        uiSelectedItems: function () {
            return this.internalSelectedItems.map(this.getLabelForValue);
        },
        uiSelectedItem: function () {
            if (this.uiSelectedItems.length <= 3) return this.uiSelectedItems.join(', ');

            const visibleItems = this.uiSelectedItems.slice(0, 2);
            const hiddenItemsCount = this.uiSelectedItems.length - visibleItems.length;
            return `${visibleItems.join(', ')} ${MeadCommon.I18N.go('mead.and')} ${hiddenItemsCount} ${MeadCommon.I18N.go('mead.more')}`;
        },
        fullSelectedItem: function () {
            let found = [];
            this.findItemWithValue(this.items, this.selectedItem, found);
            if (found.length !== 0) {
                return found[0];
            }
            return {};
        }
    },
    watch: {
        startingItem: function () {
            if (this.normalizedSelectedItems.length === 0) {
                this.setInternalSelection(this.normalizedStartingItem ? [this.normalizedStartingItem] : []);
            }
        },
        selectedItems: function () {
            this.setInternalSelection(this.normalizedSelectedItems);
        },
    },
    created () {
        if (this.usePopover) {
            MeadCommon.Flash.subscribe(this.uid, `${this.uid}-mstp-selected`, this.onPopoverItemSelected);
            MeadCommon.Flash.subscribe(this.uid, `${this.uid}-mstp-checked`, this.onPopoverCheck);
        }
    },
    destroyed () {
        if (this.usePopover) {
            MeadCommon.Flash.unsubscribe(this.uid);
            if (this.isPopoverOpen) MeadCommon.PopoverService.close();
        }
    },
};
</script>
<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.multi-step-filter-dropdown {
    .dropdown-placeholder {
        opacity: .5;
    }

    .dropdown-select-trigger {
        background: var(--c-brand-light-bg);
        color: var(--c-brand-light-fg);
        border: none;
        border-radius: $br-m;
        padding: 0 $s-s;
        font-size: $fs-m;
        font-weight: 400;
        font-family: $ff-b;
        cursor: pointer;

        &.el--s-s {
            height: 3rem;
            line-height: 1;
            font-size: $fs-s;
        }

        &.el--s-m {
            height: 4rem;
            line-height: 1;
        }

        &:hover,
        &:active {
            outline: none;
            background: var(--c-brand-default-bg);
        }
    }
}
</style>
